import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { ESTOQUE_PAGE } from "../routes";
import { SUPERVISOR_HOME_PAGE } from "../../supervisor/routes";
import { isAdminFromToken } from "../../../utils/authToken";
import {
  getProductReferencesApi,
  createProductReferenceApi,
  updateProductReferenceApi,
  deleteProductReferenceApi,
  uploadProductReferencesExcelApi,
  ProductReference
} from "../api/productReferenceApi";
import { getMassBalanceStoresApi, getStoreTrackingApi, setStoreTrackingApi } from "../api/massBalanceApi";

import { useGoBack } from "../../../hooks/useGoBack";

export default function ProductReferencesPage() {
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const isAdmin = isAdminFromToken(token);
  const goBack = useGoBack(isAdmin ? ESTOQUE_PAGE() : SUPERVISOR_HOME_PAGE());

  const [references, setReferences] = useState<ProductReference[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Modal control
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRef, setEditingRef] = useState<ProductReference | null>(null);

  // Form state
  const [numeroItem, setNumeroItem] = useState("");
  const [descricao, setDescricao] = useState("");
  const [productId, setProductId] = useState("");
  const [packSize, setPackSize] = useState(1);
  const [baseProductId, setBaseProductId] = useState("");

  // Store Tracking modal states
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [stores, setStores] = useState<any[]>([]);
  const [trackingSearch, setTrackingSearch] = useState("");
  const [trackingPage, setTrackingPage] = useState(1);
  const [trackingTotalPages, setTrackingTotalPages] = useState(1);
  const [trackingTotalItems, setTrackingTotalItems] = useState(0);
  const [trackingItems, setTrackingItems] = useState<any[]>([]);
  const [loadingTracking, setLoadingTracking] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  const loadReferences = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await getProductReferencesApi(token, {
        query: query.trim() || undefined,
        page,
        limit,
      });
      setReferences(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err: any) {
      toast.error(err.message || "Erro ao carregar referências De/Para.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferences();
  }, [token, page, query]);

  const handleOpenAdd = () => {
    setEditingRef(null);
    setNumeroItem("");
    setDescricao("");
    setProductId("");
    setPackSize(1);
    setBaseProductId("");
    setModalOpen(true);
  };

  const handleOpenEdit = (ref: ProductReference) => {
    setEditingRef(ref);
    setNumeroItem(ref.numeroItem);
    setDescricao(ref.descricao);
    setProductId(ref.productId);
    setPackSize(ref.packSize || 1);
    setBaseProductId(ref.baseProductId || "");
    setModalOpen(true);
  };

  const [deleting, setDeleting] = useState<ProductReference | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const handleDelete = async () => {
    if (!token || !deleting) return;
    try {
      setDeleteBusy(true);
      await deleteProductReferenceApi(token, deleting.id);
      toast.success("Referência excluída com sucesso!");
      setDeleting(null);
      loadReferences();
    } catch (err: any) {
      toast.error(err.message || "Erro ao excluir referência.");
    } finally {
      setDeleteBusy(false);
    }
  };

  /** "É um pack?": unitário zera o tamanho e o EAN base. */
  const isPack = packSize > 1;
  const setIsPack = (pack: boolean) => {
    if (pack) {
      setPackSize((n) => (n > 1 ? n : 2));
    } else {
      setPackSize(1);
      setBaseProductId("");
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (!numeroItem.trim() || !descricao.trim() || !productId.trim()) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }

    if (baseProductId.trim() && baseProductId.trim() === productId.trim()) {
      toast.error("O EAN Base não pode ser o mesmo do EAN do produto.");
      return;
    }

    const payload = {
      numeroItem: numeroItem.trim(),
      descricao: descricao.trim(),
      productId: productId.trim(),
      packSize: Number(packSize),
      baseProductId: baseProductId.trim() || null,
    };

    try {
      if (editingRef) {
        await updateProductReferenceApi(token, editingRef.id, payload);
        toast.success("Referência atualizada com sucesso!");
      } else {
        await createProductReferenceApi(token, payload);
        toast.success("Referência criada com sucesso!");
      }
      setModalOpen(false);
      loadReferences();
    } catch (err: any) {
      toast.error(err.message || "Erro ao salvar referência.");
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !token) return;

    const file = files[0];
    try {
      toast.info("Processando planilha...");
      const res = await uploadProductReferencesExcelApi(token, file);
      toast.success(`Upload completo! Criados: ${res.created}, Atualizados: ${res.updated}`);
      loadReferences();
    } catch (err: any) {
      toast.error(err.message || "Erro no processamento da planilha.");
    } finally {
      // Clear input
      e.target.value = "";
    }
  };

  const handleOpenStoreTracking = async () => {
    if (!token) return;
    try {
      const res = await getMassBalanceStoresApi(token);
      setStores(res || []);
      setSelectedStoreId("");
      setTrackingItems([]);
      setTrackingSearch("");
      setTrackingPage(1);
      setTrackingTotalPages(1);
      setTrackingTotalItems(0);
      setIsTrackingModalOpen(true);
    } catch (err: any) {
      toast.error(err.message || "Erro ao buscar lojas.");
    }
  };

  const loadStoreTrackingItems = async (storeId: string, searchStr: string, pageNum: number) => {
    if (!token || !storeId) return;
    try {
      setLoadingTracking(true);
      const res = await getStoreTrackingApi(token, storeId, {
        query: searchStr.trim() || undefined,
        page: pageNum,
        limit: 10
      });
      setTrackingItems(res.data || []);
      setTrackingTotalPages(res.meta?.totalPages || 1);
      setTrackingTotalItems(res.meta?.total || 0);
    } catch (err: any) {
      toast.error(err.message || "Erro ao carregar rastreamento de produtos da loja.");
    } finally {
      setLoadingTracking(false);
    }
  };

  const handleStoreChange = (storeId: string) => {
    setSelectedStoreId(storeId);
    setTrackingPage(1);
    setTrackingSearch("");
    if (storeId) {
      loadStoreTrackingItems(storeId, "", 1);
    } else {
      setTrackingItems([]);
    }
  };

  const handleSearchTracking = (e: React.FormEvent) => {
    e.preventDefault();
    setTrackingPage(1);
    loadStoreTrackingItems(selectedStoreId, trackingSearch, 1);
  };

  const handleTrackingPageChange = (newPageNum: number) => {
    setTrackingPage(newPageNum);
    loadStoreTrackingItems(selectedStoreId, trackingSearch, newPageNum);
  };

  const handleToggleStoreProductTracking = async (item: any) => {
    if (!token || !selectedStoreId) return;
    try {
      const nextState = !item.isTracked;
      await setStoreTrackingApi(token, selectedStoreId, item.id, nextState);
      toast.success(nextState ? "Produto agora é rastreado na loja!" : "Produto não é mais rastreado na loja.");
      setTrackingItems(prev => prev.map(t => t.id === item.id ? { ...t, isTracked: nextState } : t));
    } catch (err: any) {
      toast.error(err.message || "Erro ao atualizar rastreamento.");
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  if (!token) return null;

  return (
    <div className="estoqueLayout">
      <main className="estoqueMain">
        <div className="estoqueContainer">
          <EcoPageHeader
            crumbs={[...(isAdmin ? [{ label: "Estoque", onClick: () => navigate(ESTOQUE_PAGE()) }] : [{ label: "Estoque" }]), { label: "De/Para" }]}
            title="Cadastro De/Para"
            subtitle="Equivalência entre os códigos de saída (Cod Admin), os de entrada (EAN) e os produtos unitários."
            onBack={goBack}
            backLabel="Voltar"
            actions={
              <>
                <button type="button" className="ecoBtn" onClick={handleOpenStoreTracking}>
                  <EcoIcon name="store" size={18} />
                  Rastrear por loja
                </button>
                <label className="ecoBtn">
                  <EcoIcon name="download" size={18} />
                  Enviar planilha (Excel)
                  <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} style={{ display: "none" }} />
                </label>
                <button type="button" className="ecoBtn ecoBtn--primary" onClick={handleOpenAdd}>
                  <EcoIcon name="plus" size={18} />
                  Adicionar referência
                </button>
              </>
            }
          />

          <section className="estoqueControls">
            <input
              type="search"
              className="estoqueInput"
              aria-label="Buscar referência"
              placeholder="Buscar por Cod Admin, EAN ou descrição"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
          </section>

          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--eco-muted)" }}>Carregando dados...</div>
          ) : (
            <>
              <span className="refTable__count">
                {total} {total === 1 ? "referência" : "referências"}
              </span>
              <div className="estoqueTableWrapper refTable__wrap">
                <table className="estoqueTable refTable">
                  <thead>
                    <tr>
                      <th>Cod Admin (Nº Item)</th>
                      <th>Descrição</th>
                      <th>Código EAN (Product ID)</th>
                      <th>Qtd Pack (Pack Size)</th>
                      <th>EAN Base (Base Product)</th>
                      <th style={{ width: 120, textAlign: "center" }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {references.length === 0 ? (
                      <tr className="refTable__emptyRow">
                        <td colSpan={6} style={{ textAlign: "center", color: "var(--eco-muted)", padding: 24 }}>
                          Nenhuma referência encontrada.
                        </td>
                      </tr>
                    ) : (
                      references.map((ref) => (
                        <tr key={ref.id} className="refTable__row">
                          <td data-label="Cod Admin" className="refTable__code">
                            <strong>{ref.numeroItem}</strong>
                          </td>
                          <td className="refTable__desc">{ref.descricao}</td>
                          <td data-label="EAN">{ref.productId}</td>
                          <td className="refTable__kind">
                            {ref.packSize > 1 ? (
                              <span className="estoqueBadge estoqueBadge--orange">Pack c/{ref.packSize}</span>
                            ) : (
                              <span className="estoqueBadge estoqueBadge--green">Unitário</span>
                            )}
                          </td>
                          <td data-label="Conta no unitário" className={ref.baseProductId ? undefined : "refTable__optional"}>
                            {ref.baseProductId || <span style={{ color: "var(--eco-faint)" }}>-</span>}
                          </td>
                          <td className="refTable__actions">
                            <div className="refTable__buttons">
                              <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => handleOpenEdit(ref)}>
                                Editar
                              </button>
                              <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--danger" onClick={() => setDeleting(ref)}>
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="estoquePagination">
                <span>
                  Mostrando {references.length} de {total} itens
                </span>
                <div className="estoquePagination__btns">
                  <button
                    className="estoqueBtn estoqueBtn--secondary"
                    style={{ height: 32 }}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </button>
                  <span style={{ display: "inline-flex", alignItems: "center", padding: "0 12px" }}>
                    Página {page} de {totalPages}
                  </span>
                  <button
                    className="estoqueBtn estoqueBtn--secondary"
                    style={{ height: 32 }}
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Modal Add/Edit */}
      {modalOpen && (
        <div className="estoqueModalOverlay">
          <div className="estoqueModal">
            <header className="estoqueModal__header">
              <h2 className="estoqueModal__title">
                {editingRef ? "Editar Referência" : "Nova Referência"}
              </h2>
              <button className="estoqueModal__close" onClick={() => setModalOpen(false)}>
                &times;
              </button>
            </header>

            <form onSubmit={handleSave}>
              <div className="estoqueModal__body">
                <p className="refForm__intro">Liga o código das vendas ao EAN da nota fiscal.</p>

                <div className="estoqueFormGroup">
                  <label htmlFor="ref-desc">Descrição *</label>
                  <input
                    id="ref-desc"
                    type="text"
                    className="estoqueInput"
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    required
                  />
                </div>

                <div className="refForm__pair">
                  <div className="estoqueFormGroup">
                    <label htmlFor="ref-cod">Cod Admin *</label>
                    <input
                      id="ref-cod"
                      type="text"
                      className="estoqueInput"
                      value={numeroItem}
                      onChange={(e) => setNumeroItem(e.target.value)}
                      required
                    />
                    <span className="refForm__hint">Nº do item no Excel de vendas</span>
                  </div>

                  <div className="estoqueFormGroup">
                    <label htmlFor="ref-ean">EAN *</label>
                    <input
                      id="ref-ean"
                      type="text"
                      className="estoqueInput"
                      value={productId}
                      onChange={(e) => setProductId(e.target.value)}
                      required
                    />
                    <span className="refForm__hint">Product ID no XML da nota</span>
                  </div>
                </div>

                <fieldset className="refForm__kind">
                  <legend>É um pack?</legend>
                  <div className="refForm__segmented" role="radiogroup">
                    <button type="button" role="radio" aria-checked={!isPack} onClick={() => setIsPack(false)}>
                      Não, é unitário
                    </button>
                    <button type="button" role="radio" aria-checked={isPack} onClick={() => setIsPack(true)}>
                      Sim, é pack
                    </button>
                  </div>
                </fieldset>

                {isPack ? (
                  <div className="refForm__packBox">
                    <div className="refForm__pair refForm__pair--narrow">
                      <div className="estoqueFormGroup">
                        <label htmlFor="ref-qtd">Unidades *</label>
                        <input
                          id="ref-qtd"
                          type="number"
                          className="estoqueInput"
                          min={2}
                          value={packSize}
                          onChange={(e) => setPackSize(Math.max(2, Number(e.target.value) || 2))}
                        />
                      </div>
                      <div className="estoqueFormGroup">
                        <label htmlFor="ref-base">EAN do produto unitário</label>
                        <input
                          id="ref-base"
                          type="text"
                          className="estoqueInput"
                          value={baseProductId}
                          onChange={(e) => setBaseProductId(e.target.value)}
                        />
                      </div>
                    </div>
                    <span className="refForm__hint">
                      Cada venda deste pack conta como {packSize} unidades do unitário. O EAN do unitário não pode ser o
                      próprio EAN.
                    </span>
                  </div>
                ) : null}
              </div>

              <footer className="estoqueModal__footer">
                <button
                  type="button"
                  className="estoqueBtn estoqueBtn--secondary"
                  onClick={() => setModalOpen(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="estoqueBtn estoqueBtn--primary">
                  Salvar referência
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
      {/* Modal Store Product Tracking */}
      {isTrackingModalOpen && (
        <div className="estoqueModalOverlay" onClick={(e) => { if (e.target === e.currentTarget) setIsTrackingModalOpen(false); }}>
          <div className="estoqueModal" style={{ maxWidth: 700 }}>
            <header className="estoqueModal__header">
              <h2 className="estoqueModal__title">Rastrear por Loja</h2>
              <button className="estoqueModal__close" onClick={() => setIsTrackingModalOpen(false)}>
                &times;
              </button>
            </header>

            <div className="estoqueModal__body">
              <div className="estoqueFormGroup">
                <label>Selecione a Loja *</label>
                <select
                  className="estoqueSelect"
                  value={selectedStoreId}
                  onChange={(e) => handleStoreChange(e.target.value)}
                >
                  <option value="">-- Escolha uma Loja --</option>
                  {stores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nomeFantasia}
                    </option>
                  ))}
                </select>
              </div>

              {selectedStoreId && (
                <>
                  <form onSubmit={handleSearchTracking} style={{ display: "flex", gap: 8, margin: "8px 0" }}>
                    <input
                      type="text"
                      className="estoqueInput"
                      placeholder="Buscar por descrição, SKU ou código ERP..."
                      value={trackingSearch}
                      onChange={(e) => setTrackingSearch(e.target.value)}
                    />
                    <button type="submit" className="estoqueBtn estoqueBtn--primary" style={{ height: 40 }}>
                      Buscar
                    </button>
                  </form>

                  {loadingTracking ? (
                    <div style={{ textAlign: "center", padding: 24, color: "var(--eco-muted)" }}>Carregando produtos...</div>
                  ) : (
                    <>
                      <div className="estoqueTableWrapper" style={{ maxHeight: 280, overflowY: "auto" }}>
                        <table className="estoqueTable">
                          <thead>
                            <tr>
                              <th>SKU (EAN)</th>
                              <th>Descrição</th>
                              <th>Código ERP (Item)</th>
                              <th style={{ width: 140, textAlign: "center" }}>Ação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trackingItems.length === 0 ? (
                              <tr>
                                <td colSpan={4} style={{ textAlign: "center", padding: 16, color: "var(--eco-muted)" }}>
                                  Nenhum produto cadastrado no De/Para.
                                </td>
                              </tr>
                            ) : (
                              trackingItems.map((item) => (
                                <tr key={item.id}>
                                  <td>{item.productId}</td>
                                  <td>{item.descricao}</td>
                                  <td>{item.numeroItem}</td>
                                  <td style={{ textAlign: "center" }}>
                                    <button
                                      type="button"
                                      role="switch"
                                      aria-checked={!!item.isTracked}
                                      aria-label={`Rastrear ${item.descricao}`}
                                      className={`refSwitch${item.isTracked ? " refSwitch--on" : ""}`}
                                      onClick={() => handleToggleStoreProductTracking(item)}
                                    >
                                      <span className="refSwitch__label">{item.isTracked ? "Rastreado" : "Não rastreado"}</span>
                                      <span className="refSwitch__track" aria-hidden="true">
                                        <span className="refSwitch__thumb" />
                                      </span>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      {trackingTotalItems > 0 && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                          <span style={{ fontSize: 12, color: "var(--eco-muted)" }}>
                            Total: {trackingTotalItems} produtos
                          </span>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              type="button"
                              className="estoqueBtn estoqueBtn--secondary"
                              style={{ height: 28, fontSize: 11, padding: "0 8px" }}
                              disabled={trackingPage <= 1}
                              onClick={() => handleTrackingPageChange(trackingPage - 1)}
                            >
                              Anterior
                            </button>
                            <span style={{ fontSize: 12, display: "inline-flex", alignItems: "center", padding: "0 6px" }}>
                              Pág {trackingPage} de {trackingTotalPages}
                            </span>
                            <button
                              type="button"
                              className="estoqueBtn estoqueBtn--secondary"
                              style={{ height: 28, fontSize: 11, padding: "0 8px" }}
                              disabled={trackingPage >= trackingTotalPages}
                              onClick={() => handleTrackingPageChange(trackingPage + 1)}
                            >
                              Próxima
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            <footer className="estoqueModal__footer">
              <button
                type="button"
                className="estoqueBtn estoqueBtn--secondary"
                onClick={() => setIsTrackingModalOpen(false)}
              >
                Fechar
              </button>
            </footer>
          </div>
        </div>
      )}

      <EcoConfirmDialog
        open={!!deleting}
        title="Excluir referência?"
        message={
          deleting ? (
            <>
              <strong>{deleting.descricao}</strong> (Cod Admin {deleting.numeroItem}) sai do De/Para. Produto usado em
              algum balanço não pode ser excluído.
            </>
          ) : null
        }
        confirmLabel="Excluir"
        tone="danger"
        busy={deleteBusy}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
